// ============================================================
// A PLATE'S SCENE (plan body-map S2, H1): what moves on a plate that a
// looping track cannot say - the goo's drops, a hit's filter - as nodes:
// each element's key (`data-g`), tag and attributes. React draws a scene
// once; the plate's clock rebuilds it each frame and writes the same
// attributes by key, only where they changed - one description of every
// element, so the still and the moving can never disagree. Attribute names
// are the ones React and the DOM share (cx, cy, r, d, scale, dy...).
// ============================================================

import { createElement, useLayoutEffect, useRef, type ReactNode } from 'react';
import { attrNumber } from '../../../engine/plate/tracks';

export type Attrs = Record<string, number | string>;

/** What the clock writes of an element: its key (`data-g`), its attributes, and what it holds. */
export interface WrittenNode {
  key: string;
  attrs: Attrs;
  children?: WrittenNode[];
}

/** One element of the scene as React draws it: also its tag and its class. */
export interface SceneNode extends WrittenNode {
  tag: 'circle' | 'ellipse' | 'path' | 'g' | 'rect';
  className?: string;
  children?: SceneNode[];
}

export const node = (key: string, tag: SceneNode['tag'], attrs: Attrs, className?: string, children?: SceneNode[]) => ({
  key,
  tag,
  attrs,
  className,
  children,
});

/** The scene drawn: each node an element named by its key, so the clock can find it again. */
export function SceneView({ nodes }: { nodes: SceneNode[] }): ReactNode {
  return nodes.map((n) =>
    createElement(
      n.tag,
      { key: n.key, className: n.className, 'data-g': n.key, ...n.attrs },
      n.children && <SceneView nodes={n.children} />,
    ),
  );
}

/** Every node of a scene, its children's too. */
const flatten = (nodes: readonly WrittenNode[]): WrittenNode[] =>
  nodes.flatMap((n) => [n, ...flatten(n.children ?? [])]);

/** Where a scene is written: its elements by key, found once per render, and the last value written to each. */
export interface SceneWriter {
  targets: Map<string | null, { setAttribute(name: string, value: string): void }>;
  written: Map<string, string>;
}

/** A writer over the plate's scene elements, found once (the clock keeps one per render: useSceneWriter). */
export const sceneWriter = (root: SVGSVGElement): SceneWriter => ({
  targets: new Map([...root.querySelectorAll('[data-g]')].map((el) => [el.getAttribute('data-g'), el])),
  written: new Map(),
});

/**
 * Write a scene's attributes onto the elements drawn for it - rounded, and only where they changed (a node not drawn,
 * its condition off, is skipped).
 */
export function writeScene(w: SceneWriter, nodes: readonly WrittenNode[]): void {
  for (const n of flatten(nodes)) {
    const el = w.targets.get(n.key);
    if (el) writeNode(w, n, el);
  }
}

/** One node's attributes onto its element, each only if it changed. */
function writeNode(w: SceneWriter, n: WrittenNode, el: { setAttribute(name: string, value: string): void }) {
  for (const [name, raw] of Object.entries(n.attrs)) {
    const value = typeof raw === 'number' ? attrNumber(raw) : raw;
    const slot = `${n.key}|${name}`;
    if (w.written.get(slot) !== value) el.setAttribute(name, value);
    w.written.set(slot, value);
  }
}

/** Write a scene once, finding its elements afresh (the still form after the clock stops; tests). */
export const applyScene = (root: SVGSVGElement, nodes: readonly WrittenNode[]) => writeScene(sceneWriter(root), nodes);

/**
 * The plate's scene writer for its clock: the elements found on the first frame after each render (React may have
 * drawn new ones, or put its still values back), then reused frame after frame.
 */
export function useSceneWriter(): (root: SVGSVGElement) => SceneWriter {
  const writer = useRef<SceneWriter | null>(null);
  useLayoutEffect(() => {
    writer.current = null;
  });
  return (root) => (writer.current ??= sceneWriter(root));
}
